import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["canvas", "emptyState", "loadingState", "searchInput", "searchOptions", "suggestions"]
  static values = { locations: Array }

  async connect() {
    this.markersBySearchLabel = new Map()
    this.markersByNormalizedLabel = new Map()
    this.searchEntries = this.buildSearchEntries()
    this.renderSearchOptions()

    if (this.locationsValue.length === 0) {
      this.showEmptyState()
      return
    }

    this.showLoadingState()
    await this.waitForLeaflet()
    this.initializeMap()
    this.addMarkers()
  }

  disconnect() {
    if (this.map) this.map.remove()
  }

  searchChanged() {
    if (!this.hasSearchInputTarget) return

    const value = this.searchInputTarget.value.trim()
    if (value === "") {
      this.searchInputTarget.setCustomValidity("")
      this.hideSuggestions()
      return
    }

    this.searchInputTarget.setCustomValidity("")
    this.renderSuggestions(value)

    const marker = this.findMarkerForQuery(value)
    if (!marker) return

    this.focusMarker(marker)
  }

  searchSelected() {
    if (!this.hasSearchInputTarget) return

    const value = this.searchInputTarget.value.trim()
    if (value === "") return

    const marker = this.findMarkerForQuery(value)

    if (!marker) {
      this.searchInputTarget.setCustomValidity("Bitte einen Kontakt aus der Liste auswählen.")
      this.searchInputTarget.reportValidity()
      return
    }

    this.searchInputTarget.setCustomValidity("")
    this.hideSuggestions()
    this.focusMarker(marker)
  }

  searchFocused() {
    if (!this.hasSearchInputTarget) return

    const value = this.searchInputTarget.value.trim()
    if (value !== "") this.renderSuggestions(value)
  }

  searchBlurred() {
    window.setTimeout(() => this.hideSuggestions(), 150)
  }

  suggestionSelected(event) {
    const { label } = event.currentTarget.dataset
    if (!label || !this.hasSearchInputTarget) return

    this.searchInputTarget.value = label
    this.searchInputTarget.setCustomValidity("")
    this.hideSuggestions()

    const marker = this.markersBySearchLabel.get(label)
    if (marker) this.focusMarker(marker)
  }

  addMarkers() {
    const bounds = []

    for (const location of this.locationsValue) {
      const marker = window.L.marker([location.latitude, location.longitude]).addTo(this.map)
      marker.bindPopup(this.popupContent(location))
      this.registerMarker(location, marker)
      bounds.push([location.latitude, location.longitude])
    }

    this.loadingStateTarget.classList.add("hidden")

    if (bounds.length === 0) {
      this.showEmptyState()
      return
    }

    this.emptyStateTarget.classList.add("hidden")
    this.map.fitBounds(bounds, { padding: [40, 40] })

    if (this.hasSearchInputTarget && this.searchInputTarget.value.trim() !== "") {
      this.searchChanged()
    }
  }

  initializeMap() {
    this.map = window.L.map(this.canvasTarget, { scrollWheelZoom: true }).setView([47.55, 8.0], 4)

    window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap-Mitwirkende"
    }).addTo(this.map)
  }

  popupContent(location) {
    return `
      <div class="space-y-1">
        <p><strong>${this.escapeHtml(location.label)}</strong></p>
        <p>${location.count} Kontakte</p>
        <p>${this.escapeHtml(location.names.join(", "))}</p>
      </div>
    `
  }

  buildSearchEntries() {
    const nameCounts = new Map()

    this.locationsValue.forEach((location) => {
      ;(location.contacts || []).forEach((contact) => {
        const name = contact.short_name
        nameCounts.set(name, (nameCounts.get(name) || 0) + 1)
      })
    })

    return this.locationsValue
      .flatMap((location) =>
        (location.contacts || []).map((contact) => {
          const baseLabel = contact.short_name
          const label = nameCounts.get(baseLabel) > 1 ? `${baseLabel} (${location.label})` : baseLabel

          return {
            label,
            locationLabel: location.label
          }
        })
      )
      .sort((left, right) => left.label.localeCompare(right.label, "de"))
  }

  renderSearchOptions() {
    if (!this.hasSearchOptionsTarget) return

    this.searchOptionsTarget.innerHTML = this.searchEntries
      .map((entry) => `<option value="${this.escapeHtml(entry.label)}"></option>`)
      .join("")
  }

  renderSuggestions(value) {
    if (!this.hasSuggestionsTarget) return

    const normalizedValue = this.normalizeSearchValue(value)
    if (normalizedValue === "") {
      this.hideSuggestions()
      return
    }

    const matches = this.searchEntries
      .filter((entry) => this.normalizeSearchValue(entry.label).includes(normalizedValue))
      .slice(0, 8)

    if (matches.length === 0) {
      this.hideSuggestions()
      return
    }

    this.suggestionsTarget.innerHTML = matches
      .map((entry) => {
        const escapedLabel = this.escapeHtml(entry.label)

        return `
          <button
            type="button"
            class="block w-full px-4 py-3 text-left text-sm text-slate-800 hover:bg-amber-50 focus:bg-amber-50 focus:outline-none"
            data-label="${escapedLabel}"
            data-action="mousedown->contact-map#suggestionSelected touchstart->contact-map#suggestionSelected"
          >${escapedLabel}</button>
        `
      })
      .join("")

    this.suggestionsTarget.classList.remove("hidden")
  }

  hideSuggestions() {
    if (!this.hasSuggestionsTarget) return

    this.suggestionsTarget.classList.add("hidden")
    this.suggestionsTarget.innerHTML = ""
  }

  registerMarker(location, marker) {
    this.searchEntries
      .filter((entry) => entry.locationLabel === location.label)
      .forEach((entry) => {
        this.markersBySearchLabel.set(entry.label, marker)
        this.markersByNormalizedLabel.set(this.normalizeSearchValue(entry.label), marker)
      })
  }

  focusMarker(marker) {
    if (this.activeMarker === marker) {
      marker.openPopup()
      return
    }

    this.activeMarker = marker
    const targetLatLng = marker.getLatLng()
    const nextZoom = Math.max(this.map.getZoom(), 6)

    this.map.flyTo(targetLatLng, nextZoom, { duration: 0.8 })
    window.setTimeout(() => marker.openPopup(), 250)
  }

  findMarkerForQuery(value) {
    const exactMarker = this.markersBySearchLabel.get(value)
    if (exactMarker) return exactMarker

    const normalizedValue = this.normalizeSearchValue(value)
    if (normalizedValue === "") return null

    const normalizedMarker = this.markersByNormalizedLabel.get(normalizedValue)
    if (normalizedMarker) return normalizedMarker

    const prefixMatches = this.searchEntries.filter((entry) =>
      this.normalizeSearchValue(entry.label).startsWith(normalizedValue)
    )

    if (prefixMatches.length === 1) {
      return this.markersBySearchLabel.get(prefixMatches[0].label) || null
    }

    return null
  }

  async waitForLeaflet() {
    if (window.L) return

    await new Promise((resolve, reject) => {
      const startedAt = Date.now()
      const interval = window.setInterval(() => {
        if (window.L) {
          window.clearInterval(interval)
          resolve()
        } else if (Date.now() - startedAt > 5000) {
          window.clearInterval(interval)
          reject(new Error("Leaflet konnte nicht geladen werden"))
        }
      }, 50)
    })
  }

  showEmptyState() {
    this.canvasTarget.parentElement.classList.add("hidden")
    this.loadingStateTarget.classList.add("hidden")
    this.emptyStateTarget.classList.remove("hidden")
  }

  showLoadingState() {
    this.canvasTarget.parentElement.classList.remove("hidden")
    this.loadingStateTarget.classList.remove("hidden")
    this.emptyStateTarget.classList.add("hidden")
  }

  escapeHtml(value) {
    return value
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }

  normalizeSearchValue(value) {
    return value
      .normalize("NFD")
      .replaceAll(/[\u0300-\u036f]/g, "")
      .replaceAll(/\s+/g, " ")
      .trim()
      .toLocaleLowerCase("de")
  }
}
